import { ReactNode, Suspense, createContext, lazy, useCallback, useContext, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useTwoFactorModal } from '@/stores/twoFactorStore';
import { PoolDetail } from '@/types/pool';
import { DraftPoolListItem } from '@/types/pool/create';
import { TwoFactorModal } from '../components/misc/TwoFactorModal';
import { SignupModal } from '../components/signup/SignupModal';
import { UserVerificationModal } from '../components/userVerification/UserVerificationModal';

const LoginModal = lazy(() => import('../components/login/LoginModal'));
const CreatePoolModal = lazy(() => import('../components/pools/CreatePoolModal'));

type ModalContextType = {
  openLoginModal: () => void;
  openSignupModal: () => void;
  closeLoginModal: () => void;
  closeSignupModal: () => void;
  openPoolModal: (modalPayload?: { pool?: PoolDetail; draft?: DraftPoolListItem }) => void;
  closePoolModal: () => void;
  openUserVerificationModal: () => void;
  closeUserVerificationModal: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [showUserVerificationModal, setShowUserVerificationModal] = useState(false);
  const [editingPool, setEditingPool] = useState<PoolDetail | undefined>(undefined);
  const [editingDraft, setEditingDraft] = useState<DraftPoolListItem | undefined>(undefined);
  const { user, setLivenessChecked } = useAuthStore();

  // 2FA modal - only need isOpen and close
  const { isOpen: isTwoFactorModalOpen, clear } = useTwoFactorModal();

  const openLoginModal = useCallback(() => {
    setShowSignupModal(false);
    setShowLoginModal(true);
  }, []);

  const openSignupModal = useCallback(() => {
    setShowLoginModal(false);
    setShowSignupModal(true);
  }, []);

  const closeLoginModal = useCallback(() => setShowLoginModal(false), []);
  const closeSignupModal = useCallback(() => setShowSignupModal(false), []);

  const openPoolModal = useCallback((modalPayload?: { pool?: PoolDetail; draft?: DraftPoolListItem }) => {
    setEditingPool(modalPayload?.pool);
    setEditingDraft(modalPayload?.draft);
    setShowPoolModal(true);
  }, []);

  const closePoolModal = useCallback(() => {
    setShowPoolModal(false);
    setEditingPool(undefined);
  }, []);

  const openUserVerificationModal = useCallback(() => {
    setShowUserVerificationModal(true);
  }, []);

  const closeUserVerificationModal = useCallback(() => {
    setShowUserVerificationModal(false);
    setLivenessChecked(false);
  }, [setLivenessChecked]);

  return (
    <ModalContext.Provider
      value={{
        openLoginModal,
        openSignupModal,
        closeLoginModal,
        closeSignupModal,
        openPoolModal,
        closePoolModal,
        openUserVerificationModal,
        closeUserVerificationModal,
      }}
    >
      {children}
      {/* Login Modal */}
      {showLoginModal && (
        <Suspense fallback={null}>
          <LoginModal
            onClose={closeLoginModal}
            onSwitchToSignUp={() => {
              closeLoginModal();
              openSignupModal();
            }}
            onSuccess={() => {
              closeLoginModal();
            }}
          />
        </Suspense>
      )}

      {/* Sign Up Modal */}
      <SignupModal
        isOpen={showSignupModal}
        onClose={closeSignupModal}
        onSwitchToLogin={() => {
          closeSignupModal();
          openLoginModal();
        }}
      />

      {/* Pool Modal */}
      {showPoolModal && (
        <Suspense fallback={null}>
          <CreatePoolModal
            isOpen={showPoolModal}
            onClose={closePoolModal}
            editingPool={editingPool}
            editingDraft={editingDraft}
          />
        </Suspense>
      )}

      {user && (
        <UserVerificationModal
          isOpen={showUserVerificationModal}
          onClose={closeUserVerificationModal}
          onSkip={closeUserVerificationModal}
        />
      )}

      {/* Two-Factor Authentication Modal */}
      {isTwoFactorModalOpen && <TwoFactorModal isOpen={isTwoFactorModalOpen} onClose={clear} />}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
}
